import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import shopifyLogo from "@/assets/partners/shopify.svg";
import webflowLogo from "@/assets/partners/webflow.svg";
import notionLogo from "@/assets/partners/notion.svg";
import lovableLogo from "@/assets/partners/lovable.svg";
import klaviyoLogo from "@/assets/partners/klaviyo.svg";
import mailchimpLogo from "@/assets/partners/mailchimp.svg";
import zohoLogo from "@/assets/partners/zoho.svg";
import weglotLogo from "@/assets/partners/weglot.svg";
import googleLogo from "@/assets/partners/google-workspace.svg";
import kinstaLogo from "@/assets/partners/kinsta.svg";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";

const partners = [
  { name: "Shopify", logo: shopifyLogo, link: "/shopify" },
  { name: "Webflow", logo: webflowLogo, link: "/webflow" },
  { name: "Notion", logo: notionLogo, link: "/solutions/notion" },
  { name: "Lovable", logo: lovableLogo, link: "/lovable" },
  { name: "Klaviyo", logo: klaviyoLogo, link: "/solutions/klaviyo" },
  { name: "Mailchimp", logo: mailchimpLogo, link: "/solutions/mailchimp" },
  { name: "Zoho", logo: zohoLogo, link: "/solutions/zoho" },
  { name: "Weglot", logo: weglotLogo, link: "/solutions/weglot" },
  { name: "Google Workspace", logo: googleLogo, link: "/solutions/google-workspace" },
  { name: "Kinsta", logo: kinstaLogo, link: "/solutions/kinsta" },
];

export function Partners() {
  const { lang } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const prefix = lang === "ar" ? "/ar" : "";

  const trackRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const animRef = useRef<number>(0);
  const posRef = useRef(0);
  const speedRef = useRef(0.5); // px per frame

  // We render 3 copies for seamless looping
  const allPartners = [...partners, ...partners, ...partners];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Width of one set of logos
    const singleSetWidth = track.scrollWidth / 3;

    let running = true;
    const animate = () => {
      if (!running) return;
      if (!isPaused) {
        posRef.current -= speedRef.current;
        // Reset seamlessly when one full set has scrolled
        if (Math.abs(posRef.current) >= singleSetWidth) {
          posRef.current += singleSetWidth;
        }
        if (posRef.current > 0) {
          posRef.current -= singleSetWidth;
        }
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => {
      running = false;
      cancelAnimationFrame(animRef.current);
    };
  }, [isPaused]);

  // Touch/drag support
  const dragStartX = useRef(0);
  const dragStartPos = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsPaused(true);
    dragStartX.current = e.clientX;
    dragStartPos.current = posRef.current;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isPaused) return;
    const delta = e.clientX - dragStartX.current;
    const track = trackRef.current;
    if (!track) return;
    const singleSetWidth = track.scrollWidth / 3;
    let newPos = dragStartPos.current + delta;
    // Wrap around
    if (Math.abs(newPos) >= singleSetWidth) newPos += singleSetWidth;
    if (newPos > 0) newPos -= singleSetWidth;
    posRef.current = newPos;
    track.style.transform = `translateX(${newPos}px)`;
  };

  const handlePointerUp = () => {
    setIsPaused(false);
  };

  return (
    <section className="py-12 lg:py-32 bg-background border-t border-border">
      <div className="container px-6">
        <motion.p
          className="text-sm text-muted-foreground text-center mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t("Trusted partners of Kuwait's leading brands.")}
        </motion.p>

        <div
          className="overflow-hidden cursor-grab active:cursor-grabbing"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          <div
            ref={trackRef}
            className="flex items-center gap-12 sm:gap-16 w-max will-change-transform"
            style={{ touchAction: "pan-y" }}
          >
            {allPartners.map((partner, i) => (
              <Link
                key={`${partner.name}-${i}`}
                to={`${prefix}${partner.link}`}
                className="flex-shrink-0 flex items-center justify-center"
                onClick={(e) => {
                  // Prevent navigation during drag
                  if (Math.abs(posRef.current - dragStartPos.current) > 5) {
                    e.preventDefault();
                  }
                }}
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-8 sm:h-9 w-auto object-contain opacity-80"
                  draggable={false}
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
